import { Fragment, useContext, useEffect, useState, useMemo } from "react";
import { BaseModal } from "../../../../components/modal";
import { Context } from "../../../../context";
import {
  WalletFundingModalWrapper,
  ModalHeader,
  CloseButton,
  FormGroup,
  FormLabel,
  FormInput,
  AmountInputWrapper,
  HelperText,
  ModalFooter,
  CancelButton,
  ContinueButton,
  ErrorText,
  SuccessBadge,
  Step2Header,
  SectionLabel,
  DetailRow,
  TotalRow,
  StatusPill,
} from "./styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark, faCheck } from "@fortawesome/free-solid-svg-icons";
import { DotLoader } from "react-spinners";
import { PaystackButton } from "react-paystack";
import Cookies from "universal-cookie";
import { generateInvoice } from "../../../../utils/apis/wallet/generateInvoice";
import { completeFunding } from "../../../../utils/apis/wallet/completeFunding";
import { getUser } from "../../../../utils/apis/user/getUser";

const INITIAL_INVOICE_STATE = {
  invoiceId: null,
  amount: 0,
  charge: 0,
  payrollPlanName: "",
  dateInitiated: "",
  companyName: "",
  status: "",
};

export const WalletFundingModal = () => {
  const cookies = new Cookies();
  const TOKEN = cookies.get("TOKEN");
  const COMPANY_ID = cookies.get("COMPANY_ID");
  const PK = process.env.REACT_APP_PAYSTACK_PK;

  const {
    isWalletFundingModalOpen,
    setIsWalletFundingModalOpen,
    setWalletRefreshTrigger,
  } = useContext(Context);

  const [step, setStep] = useState(1); // 1 = enter amount, 2 = confirm invoice
  const [amount, setAmount] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(INITIAL_INVOICE_STATE);
  const [loggedInUser, setLoggedInUser] = useState({});

  useEffect(() => {
    if (!isWalletFundingModalOpen) return;
    getUser(TOKEN)
      .then((data) => setLoggedInUser(data))
      .catch((err) => console.error(err));
  }, [TOKEN, isWalletFundingModalOpen]);

  const resetAndClose = () => {
    setStep(1);
    setAmount("");
    setInvoice(INITIAL_INVOICE_STATE);
    setError(null);
    setIsWalletFundingModalOpen(false);
  };

  const totalDue = Number(invoice.amount || 0);
  const processingCharge = Number(invoice.charge || 0);
  const baseAmount = Number((totalDue - processingCharge).toFixed(2));
  const paystackAmount = Math.round(totalDue * 100);

  const paystackRef = useMemo(() => {
    return new Date().getTime().toString();
  }, []);

  const handleGenerateInvoice = async (e) => {
    e.preventDefault();
    setError(null);

    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setError("Enter a valid amount greater than 0.");
      return;
    }

    setIsGenerating(true);
    try {
      const data = await generateInvoice(TOKEN, COMPANY_ID, numericAmount);
      setInvoice({
        invoiceId: data?.invoiceId,
        amount: data?.amount,
        charge: data?.charge,
        payrollPlanName: data?.payrollPlanName,
        dateInitiated: data?.dateInitiated,
        companyName: data?.companyName,
        status: data?.status,
      });
      console.log(`[${Date.now()}] generateInvoice Response:`, data);
      setStep(2);
    } catch (err) {
      setError(`Could not generate invoice. ${err.message}`);
    } finally {
      setIsGenerating(false);
    }
  };

  const handlePaystackSuccess = async (paystackResponse) => {
    console.group("PAYSTACK SUCCESS");
    console.log("full callback object:", paystackResponse);
    console.log("reference:", paystackResponse?.reference);
    console.log("trxref:", paystackResponse?.trxref);
    console.log("transaction:", paystackResponse?.transaction);
    console.log("trans:", paystackResponse?.trans);
    console.log("status:", paystackResponse?.status);
    console.log("message:", paystackResponse?.message);
    if (paystackResponse?.amount !== undefined) console.log("amount:", paystackResponse.amount);
    if (paystackResponse?.currency !== undefined) console.log("currency:", paystackResponse.currency);
    console.groupEnd();

    console.group("INVOICE");
    console.log("invoiceId:", invoice.invoiceId);
    console.log("invoice.amount:", invoice.amount);
    console.log("invoice.charge:", invoice.charge);
    console.log("payrollPlanName:", invoice.payrollPlanName);
    console.groupEnd();

    console.group("CALCULATED PAYMENT");
    console.log("baseAmount:", baseAmount);
    console.log("processingCharge:", processingCharge);
    console.log("totalDue:", totalDue);
    console.log("Paystack amount in minor units:", paystackAmount);
    console.groupEnd();

    console.group("PAYSTACK CONFIGURATION");
    console.log("configured reference:", paystackRef);
    console.log("configured amount:", paystackAmount);
    console.log("configured email:", loggedInUser?.email);
    console.log("invoiceId:", invoice.invoiceId);
    console.groupEnd();

    const returnedReference = String(paystackResponse?.reference || "").trim();

    console.log("configuredReference === returnedReference", paystackRef === returnedReference);
    console.log("returnedReference:", returnedReference);
    console.log("returnedTrxref:", String(paystackResponse?.trxref || ""));

    if (!returnedReference) {
      const errMsg = "Paystack completed the callback without a usable transaction reference.";
      console.error(errMsg);
      setError(errMsg);
      return;
    }

    const fundingPayload = {
      invoiceId: Number(invoice.invoiceId),
      amountPaid: totalDue,
      payrollPlan: invoice.payrollPlanName,
      reference: returnedReference,
    };


    console.log(`[${Date.now()}] completeFunding Payload:`, JSON.stringify(fundingPayload, null, 2));

    setIsCompleting(true);
    setError(null);
    try {
      const response = await completeFunding(TOKEN, COMPANY_ID, fundingPayload);
      if (response?.status === "Success") {
        setWalletRefreshTrigger?.((prev) => (prev || 0) + 1);
        resetAndClose();
      } else {
        setError(
          response?.message ||
          "Funding could not be confirmed. Please contact support."
        );
      }
    } catch (err) {
      setError(`Could not confirm funding. ${err.message}`);
        if (err.response) {
    console.error('Server responded with:', err.response.data);
  }
    } finally {
      setIsCompleting(false);
    }
  };

  const paystackConfig = {
    reference: paystackRef,
    email: loggedInUser?.email,
    amount: paystackAmount,
    publicKey: PK,
    metadata: {
      invoiceId: invoice.invoiceId,
      amount: invoice.amount,
      charge: invoice.charge,
      companyName: invoice.companyName,
      payrollPlanName: invoice.payrollPlanName,
    },
    text: isCompleting ? "Confirming..." : "Proceed to Pay",
    onSuccess: handlePaystackSuccess,
    onClose: () => { },
  };

  const formatCurrency = (value) =>
    `\u20a6${Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <BaseModal
      open={isWalletFundingModalOpen}
      onClose={resetAndClose}
      className="wallet-funding-modal"
      height="auto"
      width="auto"
    >
      <WalletFundingModalWrapper>
        {step === 1 ? (
          <Fragment>
            <ModalHeader>
              <div className="wfm-header-text">
                <p className="wfm-title">Fund Wallet</p>
                <p className="wfm-subtitle">
                  Enter an amount to add to your company wallet.
                </p>
              </div>
              <CloseButton onClick={resetAndClose} aria-label="Close">
                <FontAwesomeIcon icon={faXmark} />
              </CloseButton>
            </ModalHeader>

            <form onSubmit={handleGenerateInvoice}>
              <FormGroup>
                <FormLabel htmlFor="wfm-amount">Amount to fund</FormLabel>
                <AmountInputWrapper>
                  <span className="wfm-currency-symbol">₦</span>
                  <FormInput
                    id="wfm-amount"
                    className="wfm-amount-input"
                    type="number"
                    min="0"
                    step="0.01"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    disabled={isGenerating}
                    required
                  />
                </AmountInputWrapper>
                <HelperText>
                  A processing charge is added to this amount and shown on
                  the next step before you pay.
                </HelperText>
              </FormGroup>

              {error && <ErrorText style={{ marginTop: "12px" }}>{error}</ErrorText>}

              <ModalFooter style={{ marginTop: "20px" }}>
                <CancelButton type="button" onClick={resetAndClose}>
                  Cancel
                </CancelButton>
                <ContinueButton type="submit" disabled={isGenerating}>
                  {isGenerating ? (
                    <DotLoader size={16} color="white" />
                  ) : (
                    "Continue"
                  )}
                </ContinueButton>
              </ModalFooter>
            </form>
          </Fragment>
        ) : (
          <Fragment>
            <Step2Header>
              <SuccessBadge>
                <FontAwesomeIcon icon={faCheck} color="#2e7d32" />
              </SuccessBadge>
              <p className="wfm-step2-title">Confirm Details</p>
              <p className="wfm-step2-subtitle">
                Review your invoice before proceeding to payment.
              </p>
            </Step2Header>

            <div>
              <SectionLabel>Invoice details</SectionLabel>
              <DetailRow>
                <span className="wfm-detail-label">Invoice ID</span>
                <span className="wfm-detail-value">#{invoice.invoiceId}</span>
              </DetailRow>
              <DetailRow>
                <span className="wfm-detail-label">Company</span>
                <span className="wfm-detail-value">{invoice.companyName}</span>
              </DetailRow>
              <DetailRow>
                <span className="wfm-detail-label">Payroll plan</span>
                <span className="wfm-detail-value">{invoice.payrollPlanName}</span>
              </DetailRow>
              <DetailRow>
                <span className="wfm-detail-label">Date initiated</span>
                <span className="wfm-detail-value">{invoice.dateInitiated}</span>
              </DetailRow>
              <DetailRow>
                <span className="wfm-detail-label">Status</span>
                <StatusPill>{invoice.status || "Awaiting payment"}</StatusPill>
              </DetailRow>
            </div>

            <div>
              <SectionLabel>Cost breakdown</SectionLabel>
              <DetailRow>
                <span className="wfm-detail-label">Amount</span>
                <span className="wfm-detail-value">
                  {formatCurrency(baseAmount)}
                </span>
              </DetailRow>
              <DetailRow>
                <span className="wfm-detail-label">Processing charge</span>
                <span className="wfm-detail-value">
                  {formatCurrency(invoice.charge)}
                </span>
              </DetailRow>
              <TotalRow>
                <span className="wfm-total-label">Total due</span>
                <span className="wfm-total-value">
                  {formatCurrency(totalDue)}
                </span>
              </TotalRow>
            </div>

            {error && <ErrorText>{error}</ErrorText>}

            <ModalFooter>
              <CancelButton
                type="button"
                onClick={() => setStep(1)}
                disabled={isCompleting}
              >
                Back
              </CancelButton>
              <div className="wfm-paystack-wrapper" style={{ flex: 1 }}>
                <PaystackButton
                  {...paystackConfig}
                  className="wfm-paystack-button"
                  disabled={isCompleting}
                />
              </div>
            </ModalFooter>
          </Fragment>
        )}
      </WalletFundingModalWrapper>
    </BaseModal>
  );
};
